import { platformApiRequest } from '../lib/platform-auth.js';

/**
 * Platform admin — scheduled-jobs observability (D-5 PR-1c). Thin typed wrappers
 * over platformApiRequest (super-admin bearer + silent refresh).
 */

export type JobRunStatus = 'running' | 'success' | 'error' | 'partial';
export type JobTrigger = 'scheduler' | 'manual';

/** Compact last-run shape embedded in the jobs list. */
export interface JobRunSummary {
  id: string;
  startedAt: string;
  completedAt: string | null;
  status: JobRunStatus;
  durationMs: number | null;
  triggeredBy: JobTrigger;
  errorMessage: string | null;
}

export interface ScheduledJob {
  name: string;
  cronPattern: string;
  timezone: string;
  description: string;
  manuallyTriggerable: boolean;
  lastRun: JobRunSummary | null;
  nextRun: string | null;
}

/** Full run row returned by the history endpoint. */
export interface JobRun {
  id: string;
  jobName: string;
  startedAt: string;
  completedAt: string | null;
  status: JobRunStatus;
  durationMs: number | null;
  dryRun: boolean;
  triggeredBy: JobTrigger;
  triggeredByUserId: string | null;
  errorMessage: string | null;
  metadata: unknown;
  createdAt: string;
  triggeredByUser: { id: string; email: string; name: string } | null;
}

export interface TriggerResult {
  runId: string;
  jobName: string;
  status: 'running';
}

export function listScheduledJobs(): Promise<{ jobs: ScheduledJob[] }> {
  return platformApiRequest<{ jobs: ScheduledJob[] }>('/v1/platform/scheduled-jobs');
}

export function listJobRuns(name: string, limit = 20): Promise<{ runs: JobRun[] }> {
  return platformApiRequest<{ runs: JobRun[] }>(
    `/v1/platform/scheduled-jobs/${encodeURIComponent(name)}/runs?limit=${limit}`,
  );
}

export function triggerJob(name: string): Promise<TriggerResult> {
  return platformApiRequest<TriggerResult>(
    `/v1/platform/scheduled-jobs/${encodeURIComponent(name)}/trigger`,
    { method: 'POST' },
  );
}
